<!--
  SubscriptionTermsSection -- the SA "Custom rates and included stores" part of
  the distributor page's subscription card (SubscriptionCompCard mounts it and
  owns the data: one GET, one record, so a comp and a rate can never disagree
  on screen).

  Gap 12 of docs/testing/subscription-billing-verification-plan-2026-10-20.md
  (Jake, 2026-10-20: build the SA UI for the per-distributor custom rate and
  the per-subscriber included-store override). Cards: "#384: Billing: price
  plan tiers with any combination of flat, active, dormant, custom-rate and
  included-store fees" (https://trello.com/c/5wMcO5Bg).

  Shows what the pinned plan version BILLS for each charge after comps and
  rates (the server reads it through the month close's own pricer), the
  allowance as plan / override / effective, and the audited history. Both
  writes are explicit actions with a REQUIRED reason and a confirm panel;
  every path in (click, Enter in a field, a form submit) only OPENS the panel,
  and the service is called solely from the Confirm button's handler (the comp
  card's review made that a P1). The server answers with the new state, which
  is handed up (`updated`) so the card re-renders from the record.
-->
<template>
    <section class="sub-terms" data-testid="sa-terms-card">
        <h2 class="sub-terms__title">Custom rates and included stores</h2>

        <p class="sub-terms__summary" data-testid="sa-terms-summary">
            <template v-if="summary && summary.sentence">{{ summary.sentence }}</template>
            <template v-else-if="summary && summary.list_text">{{ summary.list_text }}</template>
            <template v-else>
                No single price can be stated for this plan right now (comped,
                $0 or held). The per-charge amounts below are what each bills.
            </template>
        </p>

        <p v-if="!composed" class="sub-terms__note" data-testid="sa-terms-flag-note">
            Composed pricing is off: the monthly fee and the included-store
            allowance are not billed yet. Per-store rates apply today.
        </p>

        <ul class="sub-terms__charges" data-testid="sa-terms-charges">
            <li
                v-for="c in charges"
                :key="c.charge_id"
                :data-testid="`sa-terms-charge-${c.charge_id}`"
            >
                {{ chargeText(c) }}
                <button
                    v-if="c.custom_rate_cents != null && !c.comped"
                    type="button"
                    class="sub-terms__btn sub-terms__btn--small"
                    :data-testid="`sa-terms-rate-end-${c.charge_id}`"
                    :disabled="!rateReasonValid || saving"
                    @click="askEndRate(c)"
                >
                    End rate
                </button>
            </li>
        </ul>

        <p class="sub-terms__timing" data-testid="sa-terms-timing">
            A rate or an allowance applies to each month still to end,
            including the current month in full. Months that have already
            ended keep their price. A comp on a charge wins over its rate.
        </p>

        <!-- Custom rate -->
        <h3 class="sub-terms__subtitle">Custom rate</h3>
        <form
            class="sub-terms__form"
            data-testid="sa-terms-rate-form"
            @submit.prevent="askRate"
        >
            <label class="sub-terms__label" for="sa-terms-rate-charge">Charge</label>
            <select
                id="sa-terms-rate-charge"
                v-model="rateChargeId"
                class="sub-terms__input"
                data-testid="sa-terms-rate-charge"
            >
                <option value="">Choose a charge</option>
                <option
                    v-for="c in rateableCharges"
                    :key="c.charge_id"
                    :value="String(c.charge_id)"
                    :disabled="c.comped"
                >
                    {{ c.label }}{{ c.comped ? " (comped)" : "" }}
                </option>
            </select>
            <label class="sub-terms__label" for="sa-terms-rate-amount">Rate ($)</label>
            <input
                id="sa-terms-rate-amount"
                v-model="rateAmount"
                type="text"
                inputmode="decimal"
                class="sub-terms__input"
                data-testid="sa-terms-rate-amount"
                placeholder="0.00"
            />
            <label class="sub-terms__label" for="sa-terms-rate-reason">Reason (required)</label>
            <input
                id="sa-terms-rate-reason"
                v-model="rateReason"
                type="text"
                maxlength="500"
                class="sub-terms__input"
                data-testid="sa-terms-rate-reason"
            />
            <button
                type="button"
                class="sub-terms__btn"
                data-testid="sa-terms-rate-submit"
                :disabled="!rateFormValid || saving"
                @click="askRate"
            >
                Set custom rate
            </button>
        </form>

        <!-- Included stores -->
        <h3 class="sub-terms__subtitle">Included stores</h3>
        <p class="sub-terms__allowance" data-testid="sa-terms-allowance">
            <template v-if="allowance.applies">
                Plan: {{ countText(allowance.plan) }}. Override: {{ overrideText }}.
                Bills with: {{ countText(allowance.effective) }}.
            </template>
            <template v-else>
                This plan has no per-active-store price, so there is no
                included-store allowance to set.
            </template>
        </p>
        <form
            v-if="allowance.applies"
            class="sub-terms__form"
            data-testid="sa-terms-included-form"
            @submit.prevent="askIncluded"
        >
            <label class="sub-terms__label" for="sa-terms-included-input">
                Free active stores
            </label>
            <input
                id="sa-terms-included-input"
                v-model="includedCount"
                type="text"
                inputmode="numeric"
                class="sub-terms__input"
                data-testid="sa-terms-included-input"
            />
            <label class="sub-terms__label" for="sa-terms-included-reason">
                Reason (required)
            </label>
            <input
                id="sa-terms-included-reason"
                v-model="includedReason"
                type="text"
                maxlength="500"
                class="sub-terms__input"
                data-testid="sa-terms-included-reason"
            />
            <button
                type="button"
                class="sub-terms__btn"
                data-testid="sa-terms-included-submit"
                :disabled="!includedFormValid || saving"
                @click="askIncluded"
            >
                Set included stores
            </button>
            <button
                v-if="allowance.override != null"
                type="button"
                class="sub-terms__btn"
                data-testid="sa-terms-included-clear"
                :disabled="!includedReasonValid || saving"
                @click="askClearIncluded"
            >
                Follow the plan
            </button>
        </form>

        <!--
          Explicit confirmation before any write: a rate or an allowance changes
          what the distributor is billed, so one stray click or Enter must not
          do it. Cancel keeps what was typed.
        -->
        <div
            v-if="pending"
            class="sub-terms__confirm"
            role="alertdialog"
            data-testid="sa-terms-confirm"
        >
            <p>{{ pending.message }} Reason: {{ pending.reason }}</p>
            <button
                type="button"
                class="sub-terms__btn"
                data-testid="sa-terms-confirm-submit"
                :disabled="saving"
                @click="onConfirm"
            >
                Confirm
            </button>
            <button
                type="button"
                class="sub-terms__btn"
                data-testid="sa-terms-confirm-cancel"
                @click="pending = null"
            >
                Cancel
            </button>
        </div>

        <p v-if="saveError" class="sub-terms__error" data-testid="sa-terms-error">
            {{ saveError }}
        </p>

        <div v-if="history.length" data-testid="sa-terms-history">
            <h3 class="sub-terms__subtitle">Rate and allowance history</h3>
            <table class="sub-terms__table">
                <thead>
                    <tr>
                        <th>When</th>
                        <th>Change</th>
                        <th>By</th>
                        <th>Reason</th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(h, i) in history"
                        :key="`${h.at}-${i}`"
                        :data-testid="`sa-terms-history-row-${i}`"
                    >
                        <td>{{ formatDate(h.at) }}</td>
                        <td>{{ describe(h) }}</td>
                        <td>{{ h.by }}</td>
                        <td>{{ h.reason }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </section>
</template>

<script>
import { setCustomRate, setIncludedStoresOverride } from "@/services/superadminPricingService";
import { dollarsToCents } from "@/utils/dollarsToCents";

const REASON_MAX = 500;
const RATE_MAX_CENTS = 10 ** 8; // the server's magnitude-typo ceiling
const INCLUDED_MAX = 100000;

function serverMessage(e, fallback) {
    return (e && e.response && e.response.data && e.response.data.message) || fallback;
}

function validReason(text) {
    const n = text.trim().length;
    return n >= 1 && n <= REASON_MAX;
}

export default {
    name: "SubscriptionTermsSection",

    props: {
        distributorId: { type: [String, Number], required: true },
        // The record: the GET /comp payload (SubscriptionCompCard owns it).
        state: { type: Object, required: true },
    },

    emits: ["updated"],

    data() {
        return {
            saving: false,
            saveError: "",
            pending: null,
            rateChargeId: "",
            rateAmount: "",
            rateReason: "",
            includedCount: "",
            includedReason: "",
        };
    },

    computed: {
        charges() {
            return this.state.charges || [];
        },
        // Only charges on the pinned version can be repriced from here; a
        // charge only on the plan's newer version is shown but not editable.
        rateableCharges() {
            return this.charges.filter((c) => c.on_pin !== false);
        },
        composed() {
            return this.state.composed !== false;
        },
        summary() {
            return this.state.summary || null;
        },
        allowance() {
            return { applies: false, plan: null, override: null, effective: null, ...(this.state.allowance || {}) };
        },
        history() {
            return this.state.terms_history || [];
        },
        overrideText() {
            return this.allowance.override == null ? "none" : String(this.allowance.override);
        },
        rateReasonValid() {
            return validReason(this.rateReason);
        },
        rateCents() {
            const cents = dollarsToCents(this.rateAmount);
            return cents != null && cents >= 0 && cents <= RATE_MAX_CENTS ? cents : null;
        },
        rateFormValid() {
            return this.rateChargeId !== "" && this.rateCents != null && this.rateReasonValid;
        },
        includedReasonValid() {
            return validReason(this.includedReason);
        },
        includedValue() {
            const raw = this.includedCount.trim();
            if (!/^\d+$/.test(raw)) return null;
            const n = Number(raw);
            return n <= INCLUDED_MAX ? n : null;
        },
        includedFormValid() {
            return this.includedValue != null && this.includedReasonValid;
        },
    },

    methods: {
        // Every path in only OPENS the confirmation; the service is called
        // solely from onConfirm.
        askRate() {
            if (!this.rateFormValid || this.saving) return;
            const charge = this.charges.find((c) => String(c.charge_id) === this.rateChargeId);
            if (!charge) return;
            const reason = this.rateReason.trim();
            const cents = this.rateCents;
            this.pending = {
                message: `Set the custom rate for ${charge.label} to ${this.dollars(cents)} (list ${this.dollars(charge.amount_cents)})?`,
                reason,
                run: () => this.send(
                    () => setCustomRate(this.distributorId, { charge_id: charge.charge_id, amount_cents: cents, reason }),
                    "Could not set the custom rate.",
                    () => { this.rateAmount = ""; this.rateReason = ""; },
                ),
            };
        },

        askEndRate(charge) {
            if (!this.rateReasonValid || this.saving) return;
            const reason = this.rateReason.trim();
            this.pending = {
                message: `End the custom rate for ${charge.label}? It bills the list price again (${this.dollars(charge.amount_cents)}).`,
                reason,
                run: () => this.send(
                    () => setCustomRate(this.distributorId, { charge_id: charge.charge_id, amount_cents: null, reason }),
                    "Could not end the custom rate.",
                    () => { this.rateReason = ""; },
                ),
            };
        },

        askIncluded() {
            if (!this.includedFormValid || this.saving) return;
            const count = this.includedValue;
            const reason = this.includedReason.trim();
            this.pending = {
                message: `Pin this distributor to ${count} included active ${count === 1 ? "store" : "stores"}? It replaces the plan's allowance.`,
                reason,
                run: () => this.send(
                    () => setIncludedStoresOverride(this.distributorId, count, reason),
                    "Could not set the included stores.",
                    () => { this.includedCount = ""; this.includedReason = ""; },
                ),
            };
        },

        askClearIncluded() {
            if (!this.includedReasonValid || this.saving) return;
            const reason = this.includedReason.trim();
            this.pending = {
                message: "Remove the included-stores override? This distributor follows the plan's allowance again.",
                reason,
                run: () => this.send(
                    () => setIncludedStoresOverride(this.distributorId, null, reason),
                    "Could not clear the included-stores override.",
                    () => { this.includedCount = ""; this.includedReason = ""; },
                ),
            };
        },

        async onConfirm() {
            const action = this.pending;
            this.pending = null;
            if (!action || this.saving) return;
            await action.run();
        },

        // One path for every write: the server answers with the new state.
        async send(call, fallback, onSuccess) {
            this.saving = true;
            this.saveError = "";
            try {
                const resp = await call();
                this.$emit("updated", resp && resp.data ? resp.data : resp);
                onSuccess();
            } catch (e) {
                this.saveError = serverMessage(e, fallback);
            } finally {
                this.saving = false;
            }
        },

        describe(h) {
            if (h.kind === "included_stores") {
                return h.value == null
                    ? "Included-stores override removed (follows the plan)"
                    : `Included stores set to ${h.value}`;
            }
            if (h.kind === "custom_rate_ended") {
                return `Custom rate on ${h.label || "a charge"} ended (was ${this.dollars(h.amount_cents)})`;
            }
            return `Custom rate on ${h.label || "a charge"} set to ${this.dollars(h.amount_cents)}`;
        },

        // "Label: list $49.00, custom rate $19.99 (comped), bills $0.00".
        chargeText(c) {
            const parts = [`list ${this.dollars(c.amount_cents)}`];
            if (c.custom_rate_cents != null) parts.push(`custom rate ${this.dollars(c.custom_rate_cents)}`);
            let text = `${c.label}: ${parts.join(", ")}`;
            if (c.comped) text += " (comped)";
            text += c.effective_cents != null
                ? `, bills ${this.dollars(c.effective_cents)}`
                : " (on the plan's newer version, not this distributor's pinned one)";
            return text;
        },

        countText(n) {
            return n == null ? "unlimited" : String(n);
        },

        dollars(cents) {
            return `$${((cents || 0) / 100).toFixed(2)}`;
        },

        formatDate(iso) {
            if (!iso) return "";
            const d = new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);
            return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString();
        },
    },
};
</script>

<style scoped>
.sub-terms {
    margin-top: 24px;
}
.sub-terms__title {
    font-size: 1.1rem;
    margin-bottom: 8px;
}
.sub-terms__subtitle {
    font-size: 1rem;
    margin: 16px 0 8px;
}
.sub-terms__charges {
    margin: 8px 0 8px 20px;
}
.sub-terms__note,
.sub-terms__timing {
    font-size: 0.85rem;
    opacity: 0.8;
    margin-bottom: 12px;
}
.sub-terms__form {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
.sub-terms__input {
    flex: 1 1 200px;
    min-width: 140px;
    border-bottom: 1px solid currentColor;
    padding: 4px 2px;
}
.sub-terms__btn {
    padding: 6px 14px;
    border: 1px solid currentColor;
    border-radius: 4px;
}
.sub-terms__btn--small {
    padding: 2px 8px;
    margin-left: 8px;
    font-size: 0.85rem;
}
.sub-terms__btn:disabled {
    opacity: 0.4;
    cursor: not-allowed;
}
.sub-terms__confirm {
    margin-top: 12px;
    padding: 8px 12px;
    border: 1px solid currentColor;
    border-radius: 4px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
}
.sub-terms__error {
    color: #b00020;
    margin-top: 8px;
}
.sub-terms__table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
}
.sub-terms__table th,
.sub-terms__table td {
    text-align: left;
    padding: 4px 8px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);
    vertical-align: top;
}
</style>
